import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { authSessionUserSchema, type AuthSessionUser } from '@aia/platform-core';

import { authApi } from './api';

export type User = AuthSessionUser;
export type InsightRole = AuthSessionUser['role'];

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasInsightAccess: boolean;
  login: (
    email: string,
    password: string,
    turnstileToken?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  forgotPassword: (
    email: string,
    turnstileToken?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (
    token: string,
    password: string,
    turnstileToken?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  setAuth: (token: string, user: User) => void;
  logout: () => void;
}

export function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return true;
    const payload = JSON.parse(atob(parts[1])) as { exp?: number };
    return (payload.exp ?? 0) * 1000 < Date.now();
  } catch {
    return true;
  }
}

const TOKEN_KEY = 'insightpulse_token';
const USER_KEY = 'insightpulse_user';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedToken = sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
    const storedUser = sessionStorage.getItem(USER_KEY) || localStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      sessionStorage.setItem(TOKEN_KEY, storedToken);
      sessionStorage.setItem(USER_KEY, storedUser);
      setToken(storedToken);
      try {
        const parsed: unknown = JSON.parse(storedUser) as unknown;
        const result = authSessionUserSchema.safeParse(parsed);
        if (!result.success) {
          clearAuth();
          setIsLoading(false);
          return;
        }
        setUser(result.data as User);
      } catch {
        clearAuth();
        setIsLoading(false);
        return;
      }
      void verifyToken();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyToken = async () => {
    try {
      const response = await authApi.get<User>('/auth/me');
      if (response.success && response.data) {
        setUser(response.data);
      } else {
        clearAuth();
      }
    } catch {
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const clearAuth = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  // Periodic JWT expiry check
  useEffect(() => {
    const checkExpiry = () => {
      const t = sessionStorage.getItem(TOKEN_KEY);
      if (t && isTokenExpired(t)) {
        clearAuth();
      }
    };

    const interval = setInterval(checkExpiry, 60_000);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkExpiry();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [clearAuth]);

  const persistAuth = useCallback((newUser: User, newToken: string) => {
    sessionStorage.setItem(TOKEN_KEY, newToken);
    sessionStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  }, []);

  const login = useCallback(
    async (email: string, password: string, turnstileToken?: string) => {
      try {
        const response = await authApi.post<{ user: User; token: string }>('/auth/login', {
          email,
          password,
          turnstileToken,
        });

        if (response.success && response.data.user && response.data.token) {
          persistAuth(response.data.user, response.data.token);
          return { success: true };
        }
        return { success: false, error: 'Login failed' };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Login failed. Please try again.';
        return { success: false, error: errorMessage };
      }
    },
    [persistAuth],
  );

  const forgotPassword = useCallback(async (email: string, turnstileToken?: string) => {
    try {
      // A6: identify the module so the reset link lands on InsightPulse.
      const response = await authApi.post<null>('/auth/forgot-password', {
        email,
        turnstileToken,
        module: 'insight',
      });
      if (response.success) {
        return { success: true };
      }
      return { success: false, error: 'Failed to process request' };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process request';
      return { success: false, error: errorMessage };
    }
  }, []);

  const resetPassword = useCallback(
    async (resetToken: string, password: string, turnstileToken?: string) => {
      try {
        const response = await authApi.post<null>('/auth/reset-password', {
          token: resetToken,
          password,
          turnstileToken,
        });
        if (response.success) {
          return { success: true };
        }
        return { success: false, error: 'Password reset failed' };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Password reset failed. Please try again.';
        return { success: false, error: errorMessage };
      }
    },
    [],
  );

  const logout = useCallback(() => {
    void authApi.post<null>('/auth/logout').catch(() => {
      // logout error is non-critical
    });
    clearAuth();
  }, [clearAuth]);

  const hasInsightAccess =
    !!user?.enabledModules?.includes('insight') || user?.isPlatformAdmin === true;

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user && !!token,
    hasInsightAccess,
    login,
    forgotPassword,
    resetPassword,
    setAuth: (newToken: string, newUser: User) => persistAuth(newUser, newToken),
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Wraps child routes requiring authentication and the `insight` module.
 * Unauthenticated users are redirected to the platform login with a return URL.
 * Authenticated users without insight access see an "Access Denied" screen.
 */
export function ProtectedRoute({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles?: InsightRole[];
}) {
  const { user, isLoading, isAuthenticated, hasInsightAccess } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" role="status">
        <div
          className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"
          aria-hidden="true"
        />
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasInsightAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-foreground">Access Denied</h1>
          <p className="text-muted-foreground mt-2">
            Your account does not have the InsightPulse module enabled. Contact your administrator
            to request access.
          </p>
          <a href="/" className="text-secondary hover:underline mt-4 inline-block">
            Return to Platform
          </a>
        </div>
      </div>
    );
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role) && user.role !== 'PlatformAdmin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">Access Denied</h1>
          <p className="text-muted-foreground mt-2">
            You don&apos;t have the required role to view this page.
          </p>
          <a href="/dashboard" className="text-secondary hover:underline mt-4 inline-block">
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
